import Dexie from 'dexie';

// --- NEW: Helper for mimicking DOMExceptions for better compatibility ---
class DOMException extends Error {
    constructor(message: string, name: string) {
        super(message);
        this.name = name;
    }
}

class NotFoundError extends DOMException {
    constructor(message = 'The object can not be found here.') {
        super(message, 'NotFoundError');
    }
}

class TypeMismatchError extends DOMException {
    constructor(message = 'The type of an object was incompatible with the expected type.') {
        super(message, 'TypeMismatchError');
    }
}

class InvalidModificationError extends DOMException {
    constructor(message = 'The object can not be modified in this way.') {
        super(message, 'InvalidModificationError');
    }
}

// --- NEW: Path utility to ensure clean, consistent paths ---
function normalizePath(segments: string[]): string {
    const joined = segments.join('/');
    // Split by slash, filter out empty segments (from // or leading/trailing slashes)
    const parts = joined.split('/').filter(p => p.length > 0);
    // Return a path that always starts with a single '/'
    return `/${parts.join('/')}`;
}

// Define types for our file system
export type FileEntry = {
    name: string;
    path: string;
    isDirectory: boolean;
    parent: string;
    lastModified: number;
    data?: ArrayBuffer; // Data is optional for directories
};

// ... (OPFSLikeHandle and OPFSLikeWritable types remain the same)
export type OPFSLikeHandle = {
    kind: 'file' | 'directory';
    name: string;
    getFile: () => Promise<Blob>;
    isSameEntry: (other: OPFSLikeHandle) => Promise<boolean>;
};

export type OPFSLikeWritable = {
    write: (data: ArrayBufferLike | Blob) => Promise<void>;
    close: () => Promise<void>;
    abort: () => Promise<void>;
};

export class OPFSAlternative {
    readonly kind = 'directory';
    readonly name = '/'; // Root is always '/'

    private db: Dexie & { files: Dexie.Table<FileEntry, string> };

    constructor(dbName = 'opfsAlternative') {
        this.db = new Dexie(dbName) as any;
        this.db.version(1).stores({
            // Using 'path' as the primary key is more efficient for lookups
            files: '&path, name, isDirectory, parent, lastModified',
        });
    }

    public async getEntry(path: string): Promise<FileEntry | undefined> {
        return this.db.files.get(path);
    }

    async getRoot(): Promise<DirectoryHandleAlternative> {
        const rootEntry = await this.getEntry('/');
        if (!rootEntry) {
            await this.db.files.add({
                name: '',
                path: '/',
                isDirectory: true,
                parent: '', // Root has no parent
                lastModified: Date.now(),
            });
        }
        return new DirectoryHandleAlternative('/', this);
    }

    async getDirectoryHandle(
        path: string,
        options: FileSystemGetDirectoryOptions = {},
    ): Promise<DirectoryHandleAlternative> {
        const entry = await this.getEntry(path);

        if (entry) {
            if (!entry.isDirectory) {
                // CHANGED: Use specific error type
                throw new TypeMismatchError(`Entry at ${path} is a file, not a directory.`);
            }
            return new DirectoryHandleAlternative(path, this);
        }

        if (!options.create) {
            // CHANGED: Use specific error type
            throw new NotFoundError(`Directory not found: ${path}`);
        }

        const parentPath = normalizePath([path, '..']);
        const name = path.split('/').pop() || '';

        await this.db.files.add({
            name,
            path,
            isDirectory: true,
            parent: parentPath,
            lastModified: Date.now(),
        });

        return new DirectoryHandleAlternative(path, this);
    }

    async getFileHandle(
        path: string,
        options: FileSystemGetFileOptions = {},
    ): Promise<FileHandleAlternative> {
        const entry = await this.getEntry(path);

        if (entry) {
            if (entry.isDirectory) {
                // CHANGED: Use specific error type
                throw new TypeMismatchError(`Entry at ${path} is a directory, not a file.`);
            }
            return new FileHandleAlternative(path, this);
        }

        if (!options.create) {
            // CHANGED: Use specific error type
            throw new NotFoundError(`File not found: ${path}`);
        }

        const parentPath = normalizePath([path, '..']);
        const name = path.split('/').pop() || '';

        await this.db.files.add({
            name,
            path,
            isDirectory: false,
            parent: parentPath,
            lastModified: Date.now(),
            data: new ArrayBuffer(0),
        });

        return new FileHandleAlternative(path, this);
    }

    async readFile(path: string): Promise<ArrayBuffer> {
        const file = await this.getEntry(path);
        if (!file || file.isDirectory) {
            throw new NotFoundError(`File not found: ${path}`);
        }
        return file.data || new ArrayBuffer(0);
    }

    async writeFile(path: string, data: ArrayBufferLike): Promise<void> {
        const arrayBuffer = data instanceof ArrayBuffer ? data : new ArrayBuffer(data.byteLength);
        if (!(data instanceof ArrayBuffer)) {
            new Uint8Array(arrayBuffer).set(new Uint8Array(data));
        }
        await this.db.files
            .where({ path })
            .modify({ data: arrayBuffer, lastModified: Date.now() });
    }

    async listDirectory(path: string): Promise<FileEntry[]> {
        return this.db.files.where({ parent: path }).toArray();
    }

    async removeEntry(
        path: string,
        options: { recursive?: boolean } = {},
    ): Promise<void> {
        const entry = await this.getEntry(path);
        if (!entry) {
            // This is what the spec dictates: fail silently.
            return;
        }

        if (entry.isDirectory) {
            const children = await this.listDirectory(path);
            if (children.length > 0 && !options.recursive) {
                // CHANGED: Use specific error type
                throw new InvalidModificationError('Directory not empty.');
            }
            // Recursively delete children
            for (const child of children) {
                await this.removeEntry(child.path, { recursive: true });
            }
        }

        await this.db.files.delete(path);
    }

    // CHANGED: Robust implementation of resolve
    async resolve(possibleDescendant: any): Promise<string[] | null> {
        if (!(possibleDescendant instanceof DirectoryHandleAlternative || possibleDescendant instanceof FileHandleAlternative)) {
            return null;
        }

        const descendantPath = possibleDescendant.path;
        const rootPath = '/';

        if (descendantPath.startsWith(rootPath) && descendantPath !== rootPath) {
            const relativePath = descendantPath.substring(rootPath.length);
            return relativePath.split('/').filter(Boolean);
        }

        return null;
    }

    async isSameEntry(other: any): Promise<boolean> {
        return other?.kind === 'directory' && other?.name === this.name;
    }
}

export class DirectoryHandleAlternative implements FileSystemDirectoryHandle {
    readonly kind = 'directory';

    constructor(
        public readonly path: string, // Use 'path' for the full path
        private fs: OPFSAlternative,
    ) {}

    get name(): string {
        return this.path.split('/').pop() || '';
    }

    async getDirectoryHandle(
        name: string,
        options: { create?: boolean } = {},
    ): Promise<DirectoryHandleAlternative> {
        // CHANGED: Use path utility
        const newPath = normalizePath([this.path, name]);
        return this.fs.getDirectoryHandle(newPath, options);
    }

    async getFileHandle(
        name: string,
        options: { create?: boolean } = {},
    ): Promise<FileHandleAlternative> {
        // CHANGED: Use path utility
        const newPath = normalizePath([this.path, name]);
        return this.fs.getFileHandle(newPath, options);
    }

    async* entries(): AsyncGenerator<[string, DirectoryHandleAlternative | FileHandleAlternative]> {
        const entries = await this.fs.listDirectory(this.path);
        for (const entry of entries) {
            const handle = entry.isDirectory
                ? new DirectoryHandleAlternative(entry.path, this.fs)
                : new FileHandleAlternative(entry.path, this.fs);
            yield [entry.name, handle];
        }
    }

    async removeEntry(
        name: string,
        options: { recursive?: boolean } = {},
    ): Promise<void> {
        // CHANGED: Use path utility
        const pathToRemove = normalizePath([this.path, name]);
        await this.fs.removeEntry(pathToRemove, options);
    }

    async getFile(): Promise<Blob> {
        throw new TypeMismatchError('Cannot get file for a directory handle.');
    }

    async isSameEntry(other: any): Promise<boolean> {
        return other?.kind === 'directory' && other?.path === this.path;
    }

    // CHANGED: Robust implementation of resolve
    async resolve(possibleDescendant: any): Promise<string[] | null> {
        if (!(possibleDescendant instanceof DirectoryHandleAlternative || possibleDescendant instanceof FileHandleAlternative)) {
            return null;
        }

        const descendantPath = possibleDescendant.path;

        // Check if the descendant path starts with this directory's path,
        // is not the same path, and has a separator.
        if (descendantPath.startsWith(this.path) && descendantPath !== this.path) {
            // Ensure it's a direct child or grandchild, not a path with a similar prefix
            // e.g., /docs should not resolve /documents
            const nextChar = descendantPath[this.path.length];
            if (this.path === '/' || nextChar === '/') {
                const relativePath = descendantPath.substring(this.path.length);
                return relativePath.split('/').filter(Boolean);
            }
        }

        return null;
    }
}

export class FileHandleAlternative {
    readonly kind = 'file';

    constructor(
        public readonly path: string, // Use 'path' for the full path
        private fs: OPFSAlternative,
    ) {}

    // Add a getter for name to match the native API
    get name(): string {
        return this.path.split('/').pop() || '';
    }

    async getFile(): Promise<File> {
        const data = await this.fs.readFile(this.path);
        const fileName = this.name;
        const entry = await this.fs.getEntry(this.path);
        return new File([data], fileName, {
            lastModified: entry?.lastModified || Date.now(),
        });
    }

    async createWritable(): Promise<FileWritableAlternative> {
        return new FileWritableAlternative(this.path, this.fs);
    }

    async isSameEntry(other: any): Promise<boolean> {
        return other?.kind === 'file' && other?.path === this.path;
    }
}

export class FileWritableAlternative implements OPFSLikeWritable {
    // CHANGED: Use an array of chunks to handle multiple writes
    private chunks: ArrayBufferLike[];

    constructor(
        private path: string,
        private fs: OPFSAlternative,
    ) {
        this.chunks = [];
    }

    async write(data: ArrayBufferLike | Blob): Promise<void> {
        // CHANGED: Push data to the chunks array
        if (data instanceof Blob) {
            this.chunks.push(await data.arrayBuffer());
        }
        else {
            this.chunks.push(data);
        }
    }

    async close(): Promise<void> {
        // CHANGED: Combine all chunks into a single buffer before writing
        // Convert ArrayBufferLike chunks to ArrayBuffer for Blob compatibility
        const arrayBuffers = this.chunks.map(chunk =>
            chunk instanceof ArrayBuffer ? chunk : new ArrayBuffer(chunk.byteLength),
        );
        // Copy data for non-ArrayBuffer chunks
        this.chunks.forEach((chunk, index) => {
            if (!(chunk instanceof ArrayBuffer)) {
                new Uint8Array(arrayBuffers[index]).set(new Uint8Array(chunk));
            }
        });
        const blob = new Blob(arrayBuffers);
        const finalBuffer = await blob.arrayBuffer();
        await this.fs.writeFile(this.path, finalBuffer);
        this.chunks = []; // Clear memory
    }

    async abort(): Promise<void> {
        // No-op, just discard the chunks
        this.chunks = [];
    }
}
